// Typed URL state for time-bearing screens: a saved view is a URL
// (…/plants/$site/now?period=mtd&mode=live). Validated + defaulted on parse.

// `1y` is a ROLLING year of completed days, matching `_period.PERIODS` on the
// server. It is deliberately distinct from `fy`/`ytd`, which restart: at the end of
// April "the last year" means the twelve months behind you, not the three weeks
// since the financial year opened. Absent from this list the validator silently
// fell back to "today", so the 1Y chip looked selectable and showed today.
export const PERIODS = ["today", "7d", "30d", "1y", "mtd", "qtd", "fy", "ytd", "lifetime", "custom"] as const;
export type Period = (typeof PERIODS)[number];

export const MODES = ["live", "replay", "forecast"] as const;
export type Mode = (typeof MODES)[number];

export interface TimeSearch {
  period: Period;
  mode: Mode;
  from?: string; // YYYY-MM-DD, only meaningful when period === "custom"
  to?: string;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

// "today" is the default on EVERY period-bearing screen, deliberately.
//
// An earlier pass defaulted the completed-KPI screens (the KPI overview,
// Performance, the loss cascade, block detail) to 30d, on the reasoning that the
// live day carries no completed `daily_summary` row. That reasoning was wrong: it
// is true of the daily STORE, but the API has a live-day path that computes those
// figures from telemetry, so measuring /v1/kpi showed `today` returning exactly the
// same 9 MEASURED figures as `30d` — energy, PR, CUF, specific yield, CO2, revenue
// and both named availabilities all present. Site: Now reads 74% MEASURED on today.
// So the screens were never empty on today, and "now" is the right thing to open
// on; a longer window is one chip away.
//
// The one thing genuinely empty on today is the TREND panel (a trend of a single
// in-progress day has no points to plot). That belongs to the panel — Performance
// already widens its own trend window — not to the screen's default.

export function validateTimeSearch(search: Record<string, unknown>): TimeSearch {
  const period = PERIODS.includes(search["period"] as Period) ? (search["period"] as Period) : "today";
  const mode = MODES.includes(search["mode"] as Mode) ? (search["mode"] as Mode) : "live";
  const from = typeof search["from"] === "string" && ISO_DATE.test(search["from"]) ? search["from"] : undefined;
  const to = typeof search["to"] === "string" && ISO_DATE.test(search["to"]) ? search["to"] : undefined;
  return { period, mode, from, to };
}

// P7 — Equipment page. A selected equipment is a URL (…/plants/$site/equipment?equipment=INV-06).
// The header time control is URL state too (?period=30d), so the 30-day history
// window is a bookmarkable view. Today/7D/30D is all the page offers, and 30D is
// the default the wireframe shows active.
export interface EquipmentSearch {
  equipment?: string;
  period: Period;
}

const EQUIPMENT_PERIODS: Period[] = ["today", "7d", "30d"];

export function validateEquipmentSearch(search: Record<string, unknown>): EquipmentSearch {
  const equipment = typeof search["equipment"] === "string" && search["equipment"].trim() !== ""
    ? search["equipment"]
    : undefined;
  const period = EQUIPMENT_PERIODS.includes(search["period"] as Period)
    ? (search["period"] as Period)
    : "30d";
  return { equipment, period };
}

// M5 — Configuration packs. Config resolves per site (directive §5.7), so the
// selected site is URL state (…/admin/configuration?site=SITE_ID). Absent means
// absent: no site is defaulted, because another site's values are a wrong answer.
export interface ConfigurationSearch {
  site?: string;
}

export function validateConfigurationSearch(search: Record<string, unknown>): ConfigurationSearch {
  const site = typeof search["site"] === "string" && search["site"].trim() !== ""
    ? search["site"]
    : undefined;
  return { site };
}

export const FLEET_VIEWS = ["table", "cards", "heatmap", "map"] as const;
export type FleetView = (typeof FLEET_VIEWS)[number];

export const FLEET_SORTS = ["at_stake", "energy", "pr", "coverage", "site"] as const;
export type FleetSort = (typeof FLEET_SORTS)[number];

export const FLEET_DENSITIES = ["comfortable", "compact"] as const;
export type FleetDensity = (typeof FLEET_DENSITIES)[number];

export interface FleetSearch extends TimeSearch {
  view: FleetView;
  sort: FleetSort;
  filter: string;
  group: string;
  density: FleetDensity;
}

export function validateFleetSearch(search: Record<string, unknown>): FleetSearch {
  const time = validateTimeSearch(search);
  const view = FLEET_VIEWS.includes(search["view"] as FleetView) ? (search["view"] as FleetView) : "table";
  const sort = FLEET_SORTS.includes(search["sort"] as FleetSort) ? (search["sort"] as FleetSort) : "pr";
  const filter = typeof search["filter"] === "string" ? search["filter"] : "";
  const group = typeof search["group"] === "string" ? search["group"] : "";
  const density = FLEET_DENSITIES.includes(search["density"] as FleetDensity) ? (search["density"] as FleetDensity) : "comfortable";
  return { ...time, view, sort, filter, group, density };
}
